// src/utils/permissions.ts

import { UserDTO } from "../components/auth/AuthApi";

// ---- Types ---------------------------------------------------------------

export type PermissionSet = {
  canCreate: boolean;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canReport: boolean;
};

export type PermissionAction =
  | "create" | "view" | "edit" | "delete" | "report"
  | "assign" | "comment" | "upload" | "approve" | "reject"
  | "changeStatus" | "archive" | "restore" | "export" | "import" | "share"
  | "visibleInSidebar" | "visibleInDashboard";

type Quantifier = "any" | "all";

// ---- Local Storage -------------------------------------------------------

export function readUserFromLocalStorage<T = any>(): T | null {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// ---- Admin check ---------------------------------------------------------

export function isAdmin(user: any): boolean {
  const roles: string[] = Array.isArray(user?.roles) ? user.roles : [];
  return roles.map((r) => r?.toLowerCase?.()).includes("admin");
}

// ---- Mapping short action -> DTO field -----------------------------------

/** Map short action names to DTO keys like canView/canReport/etc. */
const ACTION_TO_FLAG: Record<
  PermissionAction,
  keyof NonNullable<UserDTO["permissions"]>[number]
> = {
  create: "canCreate",
  view: "canView",
  edit: "canEdit",
  delete: "canDelete",
  report: "canReport",
  assign: "canAssign",
  comment: "canComment",
  upload: "canUpload",
  approve: "canApprove",
  reject: "canReject",
  changeStatus: "canChangeStatus",
  archive: "canArchive",
  restore: "canRestore",
  export: "canExport",
  import: "canImport",
  share: "canShare",
  visibleInSidebar: "visibleInSidebar",
  visibleInDashboard: "visibleInDashboard",
};

// ---- Look up a module permission ROW ------------------------------------

/**
 * Returns the raw permission row for a module (matches by id OR name, case-insensitive),
 * or undefined if not found.
 */
export function getModulePermissionRow(
  user: UserDTO | null | undefined,
  moduleNameOrId: string
) {
  if (!user?.permissions) return undefined;
  const needle = (moduleNameOrId || "").toLowerCase();
  return user.permissions.find(
    (p) =>
      (p.moduleId && p.moduleId.toLowerCase() === needle) ||
      (p.moduleName && p.moduleName.toLowerCase() === needle)
  );
}

// ---- Robust permission checks -------------------------------------------

/**
 * Check if user has a permission (or set of permissions) for a module.
 * - `actions` can be a single action or array (e.g., ['view','report'])
 * - `quantifier`: 'any' (default) or 'all'
 */
export function hasPermission(
  user: UserDTO | null | undefined,
  moduleNameOrId: string,
  actions: PermissionAction | PermissionAction[],
  quantifier: Quantifier = "any"
): boolean {
  const mod = getModulePermissionRow(user, moduleNameOrId);
  if (!mod) return false;

  const arr = Array.isArray(actions) ? actions : [actions];
  const checks = arr.map((a) => {
    const key = ACTION_TO_FLAG[a];
    return key ? !!(mod as any)[key] : false;
  });

  return quantifier === "all" ? checks.every(Boolean) : checks.some(Boolean);
}

// ---- CRUD-only convenience (back-compat) --------------------------------

/**
 * Returns only the CRUD+report flags for a module, with Admin shortcut.
 * This replaces the previous name-colliding `getModulePermission`.
 */
export function getCrudPermissionSet(
  moduleName: string,
  userObj?: any
): PermissionSet {
  const user = userObj ?? readUserFromLocalStorage<UserDTO>();
  const empty: PermissionSet = {
    canCreate: false,
    canView: false,
    canEdit: false,
    canDelete: false,
    canReport: false,
  };

  if (!user) return empty;
  if (isAdmin(user)) {
    return { canCreate: true, canView: true, canEdit: true, canDelete: true, canReport: true };
  }

  const row = getModulePermissionRow(user, moduleName);
  if (!row) return empty;

  return {
    canCreate: !!row.canCreate,
    canView: !!row.canView,
    canEdit: !!row.canEdit,
    canDelete: !!row.canDelete,
    canReport: !!row.canReport,
  };
}

/** Check a single CRUD/report permission using the CRUD set */
export function can(
  moduleName: string,
  action: Extract<PermissionAction, "create" | "view" | "edit" | "delete" | "report">,
  userObj?: any
): boolean {
  if (!moduleName) return false;
  const p = getCrudPermissionSet(moduleName, userObj);
  switch (action) {
    case "create": return p.canCreate;
    case "view":   return p.canView;
    case "edit":   return p.canEdit;
    case "delete": return p.canDelete;
    case "report": return p.canReport;
  }
}

// ---- Multi-action helpers (string/array inputs) -------------------------

/** Normalize required -> array of PermissionAction */
function normalizeRequired(required: PermissionAction | PermissionAction[] | string): PermissionAction[] {
  if (Array.isArray(required)) return required;
  if (typeof required === "string") {
    // allow "create,edit" or "create|edit" or "create edit"
    return required
      .split(/[\s,|]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean) as PermissionAction[];
  }
  return [required];
}

/** OR semantics: allowed if user has ANY of the listed permissions (CRUD/report only) */
export function canAny(
  moduleName: string,
  required: PermissionAction | PermissionAction[] | string,
  userObj?: any
): boolean {
  const req = normalizeRequired(required).filter((a) =>
    ["create", "view", "edit", "delete", "report"].includes(a)
  ) as Extract<PermissionAction, "create" | "view" | "edit" | "delete" | "report">[];

  return req.some((a) => can(moduleName, a, userObj));
}

/** AND semantics (not used now, but handy) */
export function canAll(
  moduleName: string,
  required: PermissionAction | PermissionAction[] | string,
  userObj?: any
): boolean {
  const req = normalizeRequired(required).filter((a) =>
    ["create", "view", "edit", "delete", "report"].includes(a)
  ) as Extract<PermissionAction, "create" | "view" | "edit" | "delete" | "report">[];

  return req.every((a) => can(moduleName, a, userObj));
}

// ---- Apply permissions to action lists (buttons, menus, etc.) -----------

export type RenderMode = "hide" | "disable";
export const DEFAULT_RENDER_MODE: RenderMode = "hide";

/**
 * Applies permissions to a list of action configs.
 * - Each action may set its own `moduleName`. If omitted, `defaultModuleName` is used.
 * - Authorization uses **OR** semantics for multiple permissions (e.g. "view,report").
 * - If `renderMode` is 'hide', disallowed actions are filtered out.
 * - If 'disable', disallowed actions remain but get `disabled: true`.
 */
export function applyPermissionsToActions<
  T extends {
    required: PermissionAction | PermissionAction[] | string;
    moduleName?: string;
    disabled?: boolean;
  }
>(
  actions: T[],
  defaultModuleName: string,
  renderMode: RenderMode = DEFAULT_RENDER_MODE,
  userObj?: any
): T[] {
  if (renderMode === "hide") {
    return actions.filter((a) => {
      const moduleName = a.moduleName || defaultModuleName;
      // For UI actions that may include extended perms, allow either path:
      // 1) use robust hasPermission on the raw row (supports extended flags)
      // 2) fallback to CRUD set with canAny for basic ops
      const user = (userObj ?? readUserFromLocalStorage<UserDTO>()) as UserDTO | null;
      const ok =
        hasPermission(user, moduleName, normalizeRequired(a.required), "any") ||
        canAny(moduleName, a.required, userObj);
      return ok;
    });
  }

  // renderMode === "disable"
  return actions.map((a) => {
    const moduleName = a.moduleName || defaultModuleName;
    const user = (userObj ?? readUserFromLocalStorage<UserDTO>()) as UserDTO | null;
    const allowed =
      hasPermission(user, moduleName, normalizeRequired(a.required), "any") ||
      canAny(moduleName, a.required, userObj);
    return { ...a, disabled: a.disabled || !allowed };
  });
}